import { useMemo } from 'react';
import { css } from '@emotion/react';
import { CallIntelligenceOrgWetags } from '@wetags/call-intelligence';
import {
  DaysRemainingChip,
  useCallIntelSubscription,
  getCallIntelTrialDetails,
  TrialLocationChip,
} from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip } from '@frontend/design-system';
import { useCallIntelLocations } from './hooks/use-call-intel-locations';

type CallIntelTrialHeaderChipsProps = {
  locationIds?: string[];
};

export const CallIntelTrialHeaderChips = ({ locationIds }: CallIntelTrialHeaderChipsProps) => {
  const { t } = useTranslation('call-intelligence');
  const [isWetagEnabled, isWetagLoading] = CallIntelligenceOrgWetags.useWetag('call-intel-trial-in-app-nudges');
  const { locations, isMultiLocation } = useCallIntelLocations();
  const { trialActiveLocationIds, trialDateRangeMap, isLoading } = useCallIntelSubscription({ locationIds });

  const { locationWiseTrialDaysRemaining, aggregatedTrialDaysRemaining, hasVaryingDaysRemaining } = useMemo(
    () =>
      getCallIntelTrialDetails({
        locationIds: trialActiveLocationIds,
        trialDateRangeMap,
      }),
    [trialActiveLocationIds, trialDateRangeMap]
  );

  const trialLocations = useMemo(
    () =>
      trialActiveLocationIds.map((locationId) => ({
        id: locationId,
        name: locations[locationId] || locationId,
        daysRemaining: locationWiseTrialDaysRemaining[locationId] ?? 0,
      })),
    [trialActiveLocationIds, locations, locationWiseTrialDaysRemaining]
  );

  if (isLoading || trialActiveLocationIds.length === 0) {
    return null;
  }

  const shouldShowDaysRemainingChip = !isWetagLoading && isWetagEnabled;

  return (
    <div css={styles.wrapper} data-testid='call-intel-trial-header-chips'>
      <Chip variant='seaweed' leftElement={<Icon name='award-small' />} css={styles.freeTrialChip}>
        {t('Free Trial')}
      </Chip>

      {shouldShowDaysRemainingChip && (
        <DaysRemainingChip
          daysRemaining={aggregatedTrialDaysRemaining}
          locations={trialLocations}
          hasVaryingDaysRemaining={hasVaryingDaysRemaining}
        />
      )}

      <TrialLocationChip locations={trialLocations} isMultiLocation={!!isMultiLocation} />
    </div>
  );
};

const styles = {
  wrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  freeTrialChip: css`
    gap: ${theme.spacing(0.5)};
    padding: ${theme.spacing(0.25, 0.75)};
  `,
};
